import React, { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { images } from './core/_models'
import {AzPageHeader} from '../../../components/AzPageHeader'
import { ID, isNotEmpty, KTCard, KTCardBody, QUERIES } from '../../../../_dashboard/helpers'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import TagsComponent from '../../../../_dashboard/partials/widgets/tags/TagsComponent'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import _ from 'lodash'
import ImageUploader from '../../../../_dashboard/partials/widgets/upload/ImageUploader'
import { useQuery } from 'react-query'
import { deleteOne, getOneByIdbylang, patchOne } from '../../../services/common_requests'
import { useListView } from '../hotel-list/core/ListViewProvider'
import ImageEditModal from './image-edit-modal/ImageEditModal'
import { Facility } from '../hotel-list/core/_models'
import { useQueryResponse } from '../hotel-list/core/QueryResponseProvider'
import { useIntl } from 'react-intl'
import {toast} from 'react-toastify'
import { useConfirm } from '../../../components/ConfirmProvider'
import StickyLanguageSelector from '../../../components/StickyLanguageSelector'
import {HotelSeoTab} from './HotelSeoTab'

export interface DeleteResponse {
  payload?: {
    code?: number
  }
}

function sortedFacilityIds(facs: Facility[] | undefined): string[] {
  return [...(facs || [])]
    .map((f) => facilityStableId(f))
    .filter(Boolean)
    .sort()
}

/** Id Mongo / API pour un équipement (populate, id nu ou objet imbriqué). */
function facilityStableId(facility: unknown): string {
  if (facility == null) return ''
  if (typeof facility === 'string') {
    const t = facility.trim()
    return t
  }
  if (typeof facility !== 'object') return ''
  const f = facility as { _id?: unknown; id?: unknown }
  let raw: unknown = f._id ?? f.id
  if (raw && typeof raw === 'object') {
    const nested = raw as { _id?: unknown }
    if (nested._id != null) raw = nested._id
  }
  if (raw === null || raw === undefined || raw === '') return ''
  const s = String(raw)
  return s !== '[object Object]' ? s : ''
}

function normLang(l: unknown): string {
  return String(l ?? '').trim().toLowerCase()
}

function getImageRefId(image: unknown): string {
  if (image == null) return ''
  if (typeof image === 'string') return image.trim()
  if (typeof image !== 'object') return ''
  const raw = (image as { _id?: unknown; id?: unknown })._id ?? (image as { id?: unknown }).id
  if (raw == null || raw === '') return ''
  const s = String(raw)
  return s !== '[object Object]' ? s : ''
}

function mapTranslationForPayload(t: Record<string, unknown>) {
  const payload: Record<string, unknown> = {
    _id: t._id,
    language: normLang(t.language) || t.language,
    name: t.name,
    shortDescription: t.shortDescription,
    longDescription: t.longDescription,
    note: t.note,
  }

  const seoFields = [
    'metaTitle',
    'metaDescription',
    'metaKeywords',
    'openGraphType',
    'openGraphTitle',
    'openGraphDescription',
    'openGraphImage',
    'twitterTitle',
    'twitterDescription',
    'twitterImage',
  ] as const

  for (const field of seoFields) {
    const value = t[field]
    if (value === undefined || value === null) continue
    if (value === '') continue
    if (Array.isArray(value) && value.length === 0) continue
    payload[field] = value
  }

  return payload
}

const HotelDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: any }>()
  const [selectedImage, setSelectedImage] = useState<images | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState('fr')

  const handleImageClick = (img: images) => {
    setSelectedImage(img)
    setIsModalOpen(true)
  }
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedImage(null)
  }
  const { refetchData } = useQueryResponse()

  const handleUpdateImage = async (updatedImage: { url: string; seoUrl?: string }) => {
    // Update the album in formik state
    const updatedAlbum = formik.values.Album.map((img: images) =>
      img.url === selectedImage?.url
        ? { ...img, url: updatedImage.url, seoUrl: updatedImage.seoUrl || '' }
        : img
    )
    formik.setFieldValue('Album', updatedAlbum)

    // Create form data with the updated image only
    const formData = new FormData()
    if (updatedImage.url !== selectedImage?.url) {
      const response = await fetch(updatedImage.url)
      const blob = await response.blob()
      const file = new File([blob], 'updated-image.jpg', { type: blob.type })
      formData.append('image', file)
    }
    if (updatedImage.seoUrl) {
      formData.append('seoUrl', updatedImage.seoUrl)
    }
    setImages(updatedAlbum)
    try {
      // Call patchOne with only the updated image
      await patchOne(formData, `/images/${selectedImage?._id}`).then((response) => {
        if (response?.payload?.code === 2021) {
          //refetchData()
          handleCloseModal()
          toast.success(
            intl.formatMessage({
              id: 'ADMIN.COMMON.SUCCESS.HOTEL_UPDATED_SUCCESSFULLY',
            }),
            {
              position: 'top-right',
              autoClose: 4000,
            }
          )
        } else {
          toast.error(
            intl.formatMessage({
              id: 'ADMIN.COMMON.ERROR.HOTEL_UPDATED',
            }),
            {
              position: 'top-right',
              autoClose: 4000,
            }
          )
        }
      })
    } catch (error) {
      toast.error(
        intl.formatMessage({
          id: 'ADMIN.COMMON.ERROR.HOTEL_UPDATED',
        }),
        {
          position: 'top-right',
          autoClose: 4000,
        }
      )
    }
  }
  const [images, setImages] = useState<images[]>([])
  const [coverImageId, setCoverImageId] = useState('')
  const [settingCoverId, setSettingCoverId] = useState<string | null>(null)

  const handleImageAdded = (newImage: { url: string; _id: string }) => {
    setImages((prevImages) => [
      ...prevImages,
      {
        ...newImage,
        seoUrl: '',
        _id: newImage._id as unknown as ID,
      },
    ])
  }

  const intl = useIntl()
  const confirm = useConfirm()

  const validationSchema = Yup.object().shape({
    email: Yup.string().email(intl.formatMessage({ id: 'VALIDATION.EMAIL' })),
    address: Yup.string(),
    seoSlug: Yup.string(),
    translations: Yup.array().of(
      Yup.object().shape({
        name: Yup.string(),
        shortDescription: Yup.string(),
        longDescription: Yup.string(),
        note: Yup.string(),
        metaDescription: Yup.string(),
        metaTitle: Yup.string(),
        metaKeywords: Yup.array().of(Yup.string()),
      })
    ),
    seoKeywords: Yup.array().of(Yup.string()),
    phone: Yup.string()
      .transform((v) => (v == null ? '' : String(v).trim()))
      .test(
        'phone-format',
        intl.formatMessage({ id: 'VALIDATION.PHONE' }),
        (val) =>
          !val ||
          /^\+216\s\d{2}\s\d{3}\s\d{3}$/.test(val) ||
          /^\+[\d\s\-]{10,22}$/.test(val),
      ),
  })
  const { itemIdForUpdate, setItemIdForUpdate } = useListView()
  const enabledQuery: boolean = isNotEmpty(id)
  const { data: hotels, refetch } = useQuery(
    `${QUERIES.HOTELS_LIST}-hotel-${id}`,
    () => {
      if (!id) {
        throw new Error('ID is undefined')
      }
      return getOneByIdbylang(`/hotels/${id}?language=all`)
    },
    {
      cacheTime: 0,
      enabled: enabledQuery,
      onError: () => {
        setItemIdForUpdate(undefined)
      },
    }
  )

  const hotelFormInitialValues = useMemo(() => {
    const base = !hotels
      ? {
      id: '',
      Name: '',
      phone: '',
      email: '',
      ratings: { title: '', star: 0 },
      address: '',
      shortDescription: '',
      Facilities: [] as Facility[],
      options: [],
      themes: [],
      Note: '',
      Album: [] as images[],
      headerBackgroundImages: [] as images[],
      seoSlug: '',
      translations: [
        {
          shortDescription: '',
          note: '',
          name: '',
          longDescription: '',
          metaDescription: '',
          metaTitle: '',
          metaKeywords: [] as string[],
        },
      ],
    }
      : {
      id: hotels._id,
      phone: hotels.phone,
      email: hotels.email,
      ratings: hotels.ratings ? {...hotels.ratings} : { title: '', star: 0 },
      address: hotels.address,
      Facilities: hotels.facilities,
      options: hotels.options,
      themes: hotels.themes,
      Album: hotels.images,
      headerBackgroundImages:
        hotels.headerBackgroundImages?.length
          ? hotels.headerBackgroundImages
          : hotels.headerBackground
            ? [hotels.headerBackground]
            : [],
      seoSlug: hotels?.seoSlug,
      translations: (hotels?.translations || []).map((t: any) => ({
        ...t,
        language: normLang(t.language) || t.language,
        metaDescription: t.metaDescription ?? hotels.metaDescription ?? '',
        metaTitle: t.metaTitle ?? hotels.seoTitle ?? '',
        metaKeywords: Array.isArray(t.metaKeywords)
          ? [...t.metaKeywords]
          : Array.isArray(hotels.seoKeywords)
            ? [...hotels.seoKeywords]
            : [],
      })),
    }
    return _.cloneDeep(base)
  }, [hotels])

  const buildHotelPayload = (values: typeof hotelFormInitialValues, baseline: typeof hotelFormInitialValues) => {
    const payload: Record<string, unknown> = {}

    Object.keys(values).forEach((key) => {
      const typedKey = key as keyof typeof values

      if (key === 'Facilities') {
        const cur = sortedFacilityIds(values.Facilities as Facility[])
        const ini = sortedFacilityIds(baseline.Facilities as Facility[])
        if (!_.isEqual(cur, ini)) {
          payload.facilities = cur
        }
        return
      }

      if (key === 'id' || key === 'Album' || key === 'Name' || key === 'Note' || key === 'shortDescription' || key === 'headerBackgroundImages') {
        return
      }

      if (!_.isEqual(values[typedKey], baseline[typedKey])) {
        if (key === 'options' || key === 'themes') {
          payload[key] = (values[typedKey] as Array<{ _id: string }>).map((item) => item._id)
        } else if (key === 'translations') {
          const changedTranslations = (values.translations || []).filter((t: any) => {
            const base = (baseline.translations || []).find(
              (b: any) => normLang(b.language) === normLang(t.language),
            )
            return !base || !_.isEqual(mapTranslationForPayload(t), mapTranslationForPayload(base))
          })
          if (changedTranslations.length) {
            payload.translations = changedTranslations.map(mapTranslationForPayload)
          }
        } else {
          payload[typedKey] = values[typedKey]
        }
      }
    })

    const currentHeaderIds = (values.headerBackgroundImages || [])
      .map((img: images) => String(img._id))
      .filter(Boolean)
      .sort()
    const initialHeaderIds = (baseline.headerBackgroundImages || [])
      .map((img: images) => String(img._id))
      .filter(Boolean)
      .sort()

    if (!_.isEqual(currentHeaderIds, initialHeaderIds)) {
      payload.headerBackgroundImages = currentHeaderIds
      payload.headerBackground = currentHeaderIds.length === 1 ? currentHeaderIds[0] : null
    }

    return payload
  }

  // Initialisation de Formik — alignées sur les données chargées (évite les soumissions sans changements détectés)
  const formik = useFormik({
    initialValues: hotelFormInitialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const modifiedFields = buildHotelPayload(values, hotelFormInitialValues)
      if (Object.keys(modifiedFields).length === 0) {
        toast.info(
          intl.formatMessage({
            id: 'ADMIN.COMMON.ERROR.NO_MODIFICATION_DETECTED',
            defaultMessage: 'No changes detected.',
          }),
          { position: 'top-right', autoClose: 4000 },
        )
        return
      }
      try {
        await patchOne(modifiedFields, `/hotels/${values.id}`).then((response) => {
          const code = response?.payload?.code
          const ok =
            code === 2021 ||
            code === 2101 ||
            (response?.data != null && (code == null || code < 4000))
          if (ok) {
            toast.success(
              intl.formatMessage({
                id: 'ADMIN.COMMON.SUCCESS.HOTEL_UPDATED_SUCCESSFULLY',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            )
            refetch()
            refetchData()
          } else {
            toast.error(
              response?.payload?.message ||
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.HOTEL_UPDATED',
                }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            )
          }
        })
      } catch (error: any) {
        toast.error(
          error?.response?.data?.payload?.message ||
            intl.formatMessage({
              id: 'ADMIN.COMMON.ERROR.HOTEL_UPDATED',
            }),
          {
            position: 'top-right',
            autoClose: 4000,
          }
        )
      }
    },
  })

  const getActiveHotelTranslation = () => {
    const translations = formik.values.translations || []
    const lc = normLang(selectedLanguage)
    return (
      translations.find((t: { language?: string }) => normLang(t.language) === lc) ||
      {
        language: selectedLanguage,
        name: '',
        shortDescription: '',
        longDescription: '',
        note: '',
      }
    )
  }

  const updateActiveHotelTranslation = (field: string, value: unknown) => {
    const translations = [...(formik.values.translations || [])]
    const lc = normLang(selectedLanguage)
    const index = translations.findIndex((t: { language?: string }) => normLang(t.language) === lc)
    if (index >= 0) {
      translations[index] = {...translations[index], language: lc || selectedLanguage, [field]: value}
    } else {
      translations.push({
        language: lc || selectedLanguage,
        name: '',
        shortDescription: '',
        longDescription: '',
        note: '',
        metaKeywords: [],
        [field]: value,
      })
    }
    formik.setFieldValue('translations', translations)
  }

  useEffect(() => {
    if (hotels?.images) {
      setImages(hotels.images)
    }
    setCoverImageId(getImageRefId(hotels?.image))
  }, [hotels])

  const handleSetCoverImage = async (img: images) => {
    const imageId = getImageRefId(img)
    const hotelId = hotels?._id
    if (!imageId || !hotelId || imageId === coverImageId || settingCoverId) {
      return
    }

    try {
      setSettingCoverId(imageId)
      const response = await patchOne({ image: imageId }, `/hotels/${hotelId}`)
      const code = response?.payload?.code
      const ok =
        code === 2021 ||
        code === 2101 ||
        (response?.data != null && (code == null || code < 4000))

      if (ok) {
        setCoverImageId(imageId)
        toast.success(
          intl.formatMessage({
            id: 'HOTEL.COVER_UPDATED_SUCCESSFULLY',
            defaultMessage: 'Cover photo updated successfully',
          }),
          { position: 'top-right', autoClose: 4000 },
        )
        refetch()
      } else {
        toast.error(
          intl.formatMessage({
            id: 'HOTEL.COVER_UPDATE_ERROR',
            defaultMessage: 'Unable to update the cover photo',
          }),
          { position: 'top-right', autoClose: 4000 },
        )
      }
    } catch {
      toast.error(
        intl.formatMessage({
          id: 'HOTEL.COVER_UPDATE_ERROR',
          defaultMessage: 'Unable to update the cover photo',
        }),
        { position: 'top-right', autoClose: 4000 },
      )
    } finally {
      setSettingCoverId(null)
    }
  }

  const handleDeleteImage = async (img: images) => {
    const ok = await confirm({ message: intl.formatMessage({ id: 'HOTEL.GALLERY.DELETE_CONFIRM' }) })
    if (!ok) return
    try {
      await deleteOne(img._id, '/images').then((response: DeleteResponse) => {
        if (response?.payload?.code === 2020) {
          const deletedId = getImageRefId(img)
          setImages((prevImages) => prevImages.filter((image) => image._id !== img._id))
          if (deletedId && deletedId === coverImageId) {
            setCoverImageId('')
          }
          toast.success(
            intl.formatMessage({
              id: 'ADMIN.COMMON.SUCCESS.HOTEL_UPDATED_SUCCESSFULLY',
            }),
            {
              position: 'top-right',
              autoClose: 4000,
            }
          )
          refetch()
        } else {
          toast.error(
            intl.formatMessage({
              id: 'ADMIN.COMMON.ERROR.HOTEL_UPDATED',
            }),
            {
              position: 'top-right',
              autoClose: 4000,
            }
          )
        }
      })
    } catch (error) {
      toast.error(
        intl.formatMessage({
          id: 'ADMIN.COMMON.ERROR.HOTEL_UPDATED',
        }),
        {
          position: 'top-right',
          autoClose: 4000,
        }
      )
    }
  }

  const handleHeaderBackgroundAdded = (newImage: { url: string; _id: string }) => {
    const current = formik.values.headerBackgroundImages || []
    if (current.length >= 3) {
      toast.warning('Maximum 3 images pour le fond header')
      return
    }
    formik.setFieldValue('headerBackgroundImages', [...current, newImage])
  }

  const handleRemoveHeaderBackground = (img: images) => {
    formik.setFieldValue(
      'headerBackgroundImages',
      (formik.values.headerBackgroundImages || []).filter((image: images) => image._id !== img._id),
    )
  }

  return (
    <>
      <AzPageHeader title={intl.formatMessage({
          id: 'ADMIN.COMMON.HOTEL_DETAILS',
        })} />
      <StickyLanguageSelector
        selectedLanguage={selectedLanguage}
        onLanguageChange={setSelectedLanguage}
      />
      <KTCard>
        <KTCardBody className='py-4'>
          <div className='py-4 app-content flex-column-fluid'>
            <h1 className='my-2'>{getActiveHotelTranslation().name || hotels?.translations?.[0]?.name}</h1>
            <span className='text-muted right-spacing '>
              {intl.formatMessage({
                id: 'ADMIN.COMMON.HOTEL_ID',
              })}{' '}
              {hotels?.hotel_id}
            </span>
            <section className='gallery-section py-4'>
              <h3 className='mb-3'>Image(s) de fond header (fiche hôtel)</h3>
              <p className='text-muted mb-4'>
                1 image = pleine largeur. Jusqu&apos;à 3 images = triptyque. Laissez vide pour
                utiliser l&apos;image par défaut de la page CMS.
              </p>
              <div className='gallery gallery-style mb-4'>
                {(formik.values.headerBackgroundImages || []).map((img: images, index: number) => (
                  <div key={img._id || index} className='d-flex flex-row justify-content-space-between'>
                    <img
                      src={
                        img.url.includes('/images/')
                          ? process.env.REACT_APP_API_URL_IMAGE + img.url
                          : img.url
                      }
                      alt={`Header background ${index + 1}`}
                      className='border-image h-175px cursor-pointer'
                      onClick={() => handleImageClick(img)}
                    />
                    <div className='d-flex flex-column justify-content-space-between'>
                      <span
                        className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                        title='Remove header background'
                      >
                        <i
                          className='cursor-pointer bi bi-x fs-2 btn btn-icon btn-circle btn-active-color-primary'
                          onClick={() => handleRemoveHeaderBackground(img)}
                        ></i>
                      </span>
                    </div>
                  </div>
                ))}
                {(formik.values.headerBackgroundImages || []).length < 3 && (
                  <ImageUploader
                    onImageUpload={() => undefined}
                    onImageAdded={handleHeaderBackgroundAdded}
                    uploadEndpoint='/images'
                    hotelId={hotels?._id}
                    placement='hotel_cover'
                  />
                )}
              </div>
            </section>

            <section className='gallery-section py-4'>
              <h3 className='mb-2'>
                {intl.formatMessage({
                  id: 'HOTEL.GALLERY_IMAGES',
                  defaultMessage: "Galerie d'images",
                })}
              </h3>
              <p className='text-muted mb-4'>
                {intl.formatMessage({
                  id: 'HOTEL.GALLERY_COVER_HELP',
                  defaultMessage:
                    "Choisissez la photo de couverture de l'hôtel (affichée dans les listes). Cliquez sur l'étoile pour définir le cover.",
                })}
              </p>
              <div className='gallery gallery-style'>
                {images.map((img: images, index: number) => {
                  const imageId = getImageRefId(img)
                  const isCover = !!coverImageId && imageId === coverImageId
                  const isSettingCover = settingCoverId === imageId
                  return (
                  <div
                    key={img._id || index}
                    className={`hotel-gallery-item${isCover ? ' hotel-gallery-item--cover' : ''}`}
                  >
                    <div className='hotel-gallery-item__preview'>
                      <img
                        src={
                          img.url.includes('/images/')
                            ? process.env.REACT_APP_API_URL_IMAGE + img.url
                            : img.url
                        }
                        alt={`Gallery ${index}`}
                        className='border-image h-175px cursor-pointer'
                        onClick={() => handleImageClick(img)}
                      />
                      {isCover && (
                        <span className='hotel-gallery-item__badge'>
                          {intl.formatMessage({
                            id: 'HOTEL.COVER_BADGE',
                            defaultMessage: 'Cover',
                          })}
                        </span>
                      )}
                    </div>
                    <div className='hotel-gallery-item__actions'>
                      <span
                        className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                        data-bs-toggle='tooltip'
                        title={intl.formatMessage({
                          id: 'HOTEL.SET_AS_COVER',
                          defaultMessage: 'Set as cover',
                        })}
                      >
                        <i
                          className={`cursor-pointer bi ${
                            isCover ? 'bi-star-fill text-warning' : 'bi-star'
                          } fs-7 btn btn-icon btn-circle btn-active-color-primary${
                            isSettingCover ? ' opacity-50' : ''
                          }`}
                          onClick={() => handleSetCoverImage(img)}
                        ></i>
                      </span>
                      <span
                        className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                        data-kt-image-input-action='remove'
                        data-bs-toggle='tooltip'
                        title='Remove icon'
                      >
                        <i
                          className='cursor-pointer bi bi-pencil-fill fs-7 btn btn-icon btn-circle btn-active-color-primary'
                          onClick={() => handleImageClick(img)}
                        ></i>
                      </span>
                      <span
                        className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                        data-kt-image-input-action='remove'
                        data-bs-toggle='tooltip'
                        title='Remove icon'
                      >
                        <i
                          className='cursor-pointer bi bi-x fs-2 btn btn-icon btn-circle btn-active-color-primary'
                          onClick={() => handleDeleteImage(img)}
                        ></i>
                      </span>
                    </div>
                  </div>
                  )
                })}
                <ImageUploader
                  onImageUpload={() => undefined}
                  onImageAdded={handleImageAdded}
                  uploadEndpoint='/images'
                  hotelId={hotels?._id}
                  placement='hotel_gallery'
                />
              </div>
            </section>

            <ImageEditModal
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              selectedImage={selectedImage || { url: '', seoUrl: '' }}
              onSave={handleUpdateImage}
            />

            <div className='separator mb-4'></div>
            <div className='row'>
              <div className='col-md-12'>
                <h3>
                  {' '}
                  {intl.formatMessage({
                    id: 'ADMIN.COMMON.GENERAL_INFORMATION',
                  })}
                </h3>
                <form onSubmit={formik.handleSubmit}>
                  <div className='form-group mb-4'>
                    <div className='d-flex flex-wrap align-items-center'>
                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {' '}
                            {intl.formatMessage({
                              id: 'COLUMNS.CATEGORY',
                            })}
                          </strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          name='ratings.title'
                          value={formik.values.ratings.title}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled
                        />
                        {formik.touched.ratings &&
                          typeof formik.touched.ratings === 'object' &&
                          'title' in formik.touched.ratings &&
                          formik.errors.ratings &&
                          typeof formik.errors.ratings === 'object' &&
                          'title' in formik.errors.ratings && (
                            <div className='text-danger'>
                              {formik.errors.ratings.title as string}
                            </div>
                          )}
                      </div>
                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {' '}
                            {intl.formatMessage({
                              id: 'COLUMNS.ADDRESS',
                            })}
                          </strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          name='address'
                          value={formik.values.address}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.address && formik.errors.address && (
                          <div className='text-danger'>{formik.errors.address as string}</div>
                        )}
                      </div>

                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {' '}
                            {intl.formatMessage({
                              id: 'COLUMNS.NAME',
                            })}
                          </strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          value={getActiveHotelTranslation().name || ''}
                          onChange={(e) => updateActiveHotelTranslation('name', e.target.value)}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.Name && formik.errors.Name && (
                          <div className='text-danger'>{formik.errors.Name as string}</div>
                        )}
                      </div>


                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {' '}
                            {intl.formatMessage({
                              id: 'COLUMNS.PHONE',
                            })}
                          </strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          name='phone'
                          value={formik.values.phone}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.phone && formik.errors.phone && (
                          <div className='text-danger'>{formik.errors.phone as string}</div>
                        )}
                      </div>
                      <div className='me-4 col-12 col-md-auto mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {' '}
                            {intl.formatMessage({
                              id: 'COLUMNS.EMAIL',
                            })}
                          </strong>
                        </label>
                        <input
                          type='text'
                          className='form-control w-100 w-md-auto'
                          name='email'
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.email && formik.errors.email && (
                          <div className='text-danger'>{formik.errors.email as string}</div>
                        )}
                      </div>
                      <div className='form-group mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {' '}
                            {intl.formatMessage({
                              id: 'COLUMNS.SHORT_DESCRIPTION',
                            })}
                          </strong>
                        </label>
                        <ReactQuill
                          value={getActiveHotelTranslation().shortDescription || ''}
                          onChange={(value) => updateActiveHotelTranslation('shortDescription', value)}
                          onBlur={() => formik.setFieldTouched('translations', true)}
                          modules={{
                            toolbar: [
                              ['bold', 'italic', 'underline', 'strike'],
                              ['blockquote'],
                              [{ header: 1 }, { header: 2 }],
                              [{ list: 'ordered' }, { list: 'bullet' }],
                              [{ indent: '-1' }, { indent: '+1' }],
                              [{ direction: 'rtl' }],
                              [{ size: ['small', false, 'large', 'huge'] }],
                              [{ header: [1, 2, 3, 4, 5, 6, false] }],
                              [{ font: [] }],
                              ['clean'],
                            ],
                          }}
                          formats={[
                            'header',
                            'font',
                            'size',
                            'bold',
                            'italic',
                            'underline',
                            'strike',
                            'blockquote',
                            'list',
                            'bullet',
                            'indent',
                            'link',
                            'image',
                            'video',
                          ]}
                        />
                      </div>
                      <div className='form-group mb-3'>
                        <label className='me-3 mb-3'>
                          <strong>
                            {' '}
                            {intl.formatMessage({
                              id: 'COLUMNS.LONG_DESCRIPTION',
                            })}
                          </strong>
                        </label>
                        <ReactQuill
                          value={getActiveHotelTranslation().longDescription || ''}
                          onChange={(value) => updateActiveHotelTranslation('longDescription', value)}
                          onBlur={() => formik.setFieldTouched('translations', true)}
                          modules={{
                            toolbar: [
                              ['bold', 'italic', 'underline', 'strike'],
                              ['blockquote'],
                              [{ header: 1 }, { header: 2 }],
                              [{ list: 'ordered' }, { list: 'bullet' }],
                              [{ indent: '-1' }, { indent: '+1' }],
                              [{ direction: 'rtl' }],
                              [{ size: ['small', false, 'large', 'huge'] }],
                              [{ header: [1, 2, 3, 4, 5, 6, false] }],
                              [{ font: [] }],
                              ['clean'],
                            ],
                          }}
                          formats={[
                            'header',
                            'font',
                            'size',
                            'bold',
                            'italic',
                            'underline',
                            'strike',
                            'blockquote',
                            'list',
                            'bullet',
                            'indent',
                            'link',
                            'image',
                            'video',
                          ]}
                        />
                      </div>
                    </div>
                  </div>

                  <HotelSeoTab formik={formik} intl={intl} selectedLanguage={selectedLanguage} />

                  <div className='separator mb-4'></div>
                  <h3>
                    {intl.formatMessage({
                      id: 'COLUMNS.FACILITIES',
                    })}
                  </h3>
                  <div className=' mb-4'>
                    <TagsComponent
                      initialValues={
                        (formik.values.Facilities?.map((facility: Facility) => facilityStableId(facility)).filter(
                          Boolean,
                        ) as string[]) || []
                      }
                      onValuesChange={(updatedFacilityIds) => {
                        const newFacilities = updatedFacilityIds.map((fid) => {
                          const existing = formik.values.Facilities.find(
                            (f: Facility) => facilityStableId(f) === String(fid),
                          )
                          return existing ?? { _id: fid, translations: [{ title: '' }] }
                        })
                        formik.setFieldValue('Facilities', newFacilities)
                      }}
                      type="facilities"
                      selectedLanguage={selectedLanguage}
                    />
                  </div>
                  <div className='separator mb-4'></div>
                  <h3>
                    {intl.formatMessage({
                      id: 'COLUMNS.THEMES',
                    })}
                  </h3>
                  <div className=' mb-4'>
                    <TagsComponent

                      initialValues={
                        (formik.values.themes
                          ?.map((theme: { _id?: string }) =>
                            theme._id != null && theme._id !== '' ? String(theme._id) : '',
                          )
                          .filter(Boolean) as string[]) || []
                      }
                      onValuesChange={(updatedthemes) => {
                        const newThemes = updatedthemes.map((tid) => {
                          const fid = String(tid)
                          const existing = formik.values.themes.find(
                            (o: { _id?: string }) => String(o._id ?? '') === fid,
                          )
                          return existing ?? { _id: fid, translations: [{ name: '' }] }
                        })
                        formik.setFieldValue('themes', newThemes)
                      }}
                      type='themes'
                      selectedLanguage={selectedLanguage}
                    />
                  </div>
                  <div className='separator mb-4'></div>
                  <h3>
                    {intl.formatMessage({
                      id: 'COLUMNS.OPTIONS',
                    })}
                  </h3>
                  <div className=' mb-4'>
                    <TagsComponent
                      initialValues={
                        (formik.values.options
                          ?.map((option: { _id?: string }) =>
                            option._id != null && option._id !== '' ? String(option._id) : '',
                          )
                          .filter(Boolean) as string[]) || []
                      }
                      onValuesChange={(updatedOptions) => {
                        const newOptions = updatedOptions.map((oid) => {
                          const fid = String(oid)
                          const existing = formik.values.options.find(
                            (o: { _id?: string }) => String(o._id ?? '') === fid,
                          )
                          return existing ?? { _id: fid, translations: [{ title: '' }] }
                        })
                        formik.setFieldValue('options', newOptions)
                      }}
                      selectedLanguage={selectedLanguage}
                      type='options'
                    />
                  </div>
                  <div className='separator mb-4'></div>
                  <h3>
                    {intl.formatMessage({
                      id: 'COLUMNS.NOTE',
                    })}
                  </h3>
                  <div className='form-group mb-3'>
                    <ReactQuill
                      value={getActiveHotelTranslation().note || ''}
                      onChange={(value) => updateActiveHotelTranslation('note', value)}
                      onBlur={() => formik.setFieldTouched('translations', true)}
                      modules={{
                        toolbar: [
                          ['bold', 'italic', 'underline', 'strike'],
                          ['blockquote'],
                          [{ header: 1 }, { header: 2 }],
                          [{ list: 'ordered' }, { list: 'bullet' }],
                          [{ indent: '-1' }, { indent: '+1' }],
                          [{ direction: 'rtl' }],
                          [{ size: ['small', false, 'large', 'huge'] }],
                          [{ header: [1, 2, 3, 4, 5, 6, false] }],
                          [{ font: [] }],
                          ['clean'],
                        ],
                      }}
                      formats={[
                        'header',
                        'font',
                        'size',
                        'bold',
                        'italic',
                        'underline',
                        'strike',
                        'blockquote',
                        'list',
                        'indent',
                        'link',
                        'image',
                        'video',
                      ]}
                    />
                    {formik.touched.Note && formik.errors.Note && (
                      <div className='text-danger'>{formik.errors.Note as string}</div>
                    )}
                  </div>
                  <div className='separator mb-4'></div>
                  <div className='modal-footer'>
                    <button type='submit' className='btn btn-primary me-2'>
                      {intl.formatMessage({
                        id: 'HEADER.SAVE',
                      })}
                    </button>
                    <button type='button' className='btn btn-secondary '>
                      {intl.formatMessage({
                        id: 'HEADER.RESET',
                      })}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </KTCardBody>
      </KTCard>
    </>
  )
}

export { HotelDetailsPage }
